import gsap from "gsap";

import { Howl } from "howler";

// Audio alias, Howl
const audioMap: Map<string, Howl> = new Map();

export function registerSound(alias: string, src: string): void {
  // Check if it already exists to avoid double-init during hot reloads
  if (!audioMap.has(alias)) {
    audioMap.set(alias, new Howl({ src: [src] }));
  }
}

// Our setup with optional configs makes it impossible to dynamically infer
// the audio file alias and support autocompletion, which is unfortunate, so
// we use a string. Same with regular asset aliases.
export abstract class Audio {
  public static play(alias: string, loop: boolean = false, volume: number = 1.0): void {
    const howl = this.getHowl(alias);
    if (!howl) return;

    const id = howl.play();
    howl.loop(loop, id);
    howl.volume(volume, id);
  }

  public static stop(alias: string): void {
    this.getHowl(alias)?.stop();
  }

  // Tweak Howl instance paramters if you like...
  public static get(alias: string): Howl | undefined {
    return audioMap.get(alias);
  }

  public static volume(alias: string, vol: number): void {
    Audio.get(alias)?.volume(vol);
  }

  public static fadeOutToEnd(alias: string, targetVolume: number = 0, complete?: () => void): void {
    const howl = this.getHowl(alias);
    if (!howl || !howl.playing()) return;

    const duration = howl.duration();
    const currentTime = (howl.seek() as number) || 0;
    let remainingTime = duration - currentTime;

    if (remainingTime <= 0) remainingTime = 0.5;

    // Use our standard fade method for the heavy lifting
    this.fade(alias, targetVolume, remainingTime, complete);
  }

  public static fade(alias: string, targetVolume: number, duration: number = 0.5, complete?: () => void): void {
    const howl = this.getHowl(alias);
    if (!howl) return;

    // 1. Kill any existing volume tweens on this sound instance
    // This prevents "tween fighting" if fade is called rapidly.
    gsap.killTweensOf(howl, "volume");

    // 2. Proxy object to bridge GSAP and Howler's volume() method
    const volumeState = { value: howl.volume() };

    gsap.to(volumeState, {
      value: targetVolume,
      duration: duration,
      ease: "expo.out",
      onUpdate: () => {
        howl.volume(volumeState.value);
      },
      onComplete: () => {
        // If we faded to zero, stop the sound to save on voices
        if (targetVolume === 0) {
          howl.stop();
          howl.volume(1); // Reset for next time
          complete?.();
        }
      },
      onInterrupt: () => {
        // If we faded to zero, stop the sound to save on voices
        if (targetVolume === 0) {
          howl.stop();
          howl.volume(1); // Reset for next time
          complete?.();
        }
      }
    });
  }

  /**
   * This method solely exists to throw warning messages if audio is not found.
   */
  private static getHowl(alias: string): Howl | undefined {
    const howl: Howl | undefined = audioMap.get(alias);

    if (!howl) {
      console.warn(`Audio file ${alias} not found.`);

      return undefined;
    }

    return howl;
  }
}

